<script setup>

import {ref, reactive, computed, onBeforeMount} from "vue";
import {useItemStore} from "@/stores/ItemStore";
import {useRoute} from "vue-router";
import ScormPlayer from "@/components/players/ScormPlayer";
import ContentPlayer from '@/components/players/ContentPlayer';
import LoadingSpinner from "@/shared/LoadingSpinner";
import {getReturnPath} from "@/Helper";
import {useRouter} from "vue-router";
import Button from "@/shared/Button";
import ConfirmationModal from "@/components/modal/Confirmation";
import SuccessModal from "@/components/modal/SuccessModal";
import CompleteButtonSvg from "@/components/item/CompleteButtonSvg";
import confetti from "canvas-confetti";

const router = useRouter();
const route = useRoute();
const store = useItemStore();
const isScorm = computed(() => store.item && store.item.vertical === 'INTERACTIVE CONTENT')
const state = reactive({
    loading: false,
    showHereButton: true,
    showDashboardButton: true,
});

async function getContentItem() {
    state.loading = true;
    await store.getContentItem(route.params.id, route.params.mission);
    document.title = store.item.name;
    state.loading = false;
    if (!isScorm.value) {
        await store.startContentItem(route.params.id, route.params.mission);
    }
}

const isModalOpen = ref(false);
const isSuccessModalOpen = ref(false);

const showSuccessModal = () => {
    isSuccessModalOpen.value = true;
};
const hideSuccessModal = () => {
    isSuccessModalOpen.value = false;
};
const showModal = () => {
    isModalOpen.value = true;
};
const hideModal = () => {
    isModalOpen.value = false;
};
const celebrate = () => {
    confetti({
        angle: randomInRange(550, 1250),
        spread: randomInRange(500, 700),
        particleCount: randomInRange(500, 1000),
        origin: {y: 0.6}
    });
};

const confirmCompletion = async () => {
    await store.completeContentItem(route.params.id, route.params.mission);
    hideModal();
    showSuccessModal();
    store.item.completed = true;
    celebrate();
};

const ScormEvent = async (event) => {
    if (event.status === 'completed') {
        await confirmCompletion();
        return;
    }
    if (event.status === 'exist') {
        state.showHereButton = false;
        hideModal();
        showSuccessModal();
        return;
    }
}

function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
}

function goToPath(event) {
    event.preventDefault();

    const returnPath = getReturnPath();

    if (returnPath) {
        window.location.href = returnPath;
    } else {
        router.push({name: 'dashboard'});
    }
}

onBeforeMount(async () => {
    await getContentItem();
});

</script>

<template>
    <div class="font-ubuntu relative flex min-h-screen flex-col bg-gray-100">
        <div class="bg-gray-800 py-3 sticky top-0 z-10 text-xs text-white">
            <div class="container mx-auto flex items-center justify-between px-4 lg:px-0">
                <div class="flex items-center ltr:space-x-6 rtl:space-x-reverse-6">
                    <a @click="goToPath" href="#" class="flex items-center ltr:space-x-2 rtl:space-x-reverse-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                             stroke="currentColor" class="h-6 w-6 rtl:scale-x-[-1] ltr:ml-0 rtl:ml-2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"/>
                        </svg>
                        <span class="ltr:ml-0 rtl:ml-2">{{ $tt('content_page__header_back_button') }}</span>
                    </a>
                    <span class="text-sm lg:text-base ltr:ml-0 rtl:ml-4">{{ store.item.name }}</span>
                </div>
                <div class="flex items-center space-x-6">
                    <Button
                        class="hidden items-center space-x-2 rtl:space-x-reverse rounded-md bg-lime-600 px-5 py-2 md:flex"
                        :class="{'cursor-not-allowed bg-gray-600': isScorm }"
                        :disabled="isScorm"
                        @click="showModal"
                        v-if="!store.item.completed"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                             stroke="currentColor" class="h-6 w-6">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <span class="">{{ $tt('content_page__header_complete_button') }}</span>
                    </Button>
                    <Button
                        disabled="true"
                        class="hidden items-center space-x-2 rtl:space-x-reverse rounded-md bg-custom-yellow px-5 py-2 md:flex"
                        v-else>
                        <CompleteButtonSvg/>
                        <span class="">{{ $tt('content_page__header_completed_button') }}</span>
                    </Button>
                    <div class="cursor-pointer"></div>
                </div>
            </div>
        </div>

        <div v-if="state.loading"
             class="w-full h-full fixed block top-0 left-0 bg-white opacity-75 z-50 flex items-center justify-center">
            <LoadingSpinner/>
        </div>

        <div class="flex flex-1 flex-col items-center text-gray-700 justify-center"
             v-if="!state.loading && store.item">
            <ScormPlayer v-if="store.item.vertical === 'INTERACTIVE CONTENT'" :content="store.item" @scorm-completed="ScormEvent"/>
            <ContentPlayer v-else :content="store.item"></ContentPlayer>
        </div>

        <div class="bg-gray-800 px-6 py-4 md:hidden">
          <Button
              :class="{ ' cursor-not-allowed bg-gray-600': isScorm }"
              v-if="!store.item.completed"
              :disabled="isScorm"
              @click="showModal"
              class="flex w-full items-center justify-center space-x-2 rtl:space-x-reverse rounded-md bg-lime-600 py-2 text-white">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                 stroke="currentColor" class="h-4 w-4">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="">{{ $tt('content_page__header_complete_button') }}</span>
          </Button>
          <Button
              disabled="true"
              class="px-5 md:flex w-full items-center justify-center space-x-2 rtl:space-x-reverse rounded-md bg-custom-yellow py-2"
              v-else>
            <span class="">{{ $tt('content_page__header_completed_button') }}</span>
          </Button>
        </div>
    </div>

    <ConfirmationModal :open="isModalOpen" @confirm="confirmCompletion" @cancel="hideModal">
        <p>{{ $tt('content_page__confirmation_message') }}</p>
    </ConfirmationModal>

    <SuccessModal
        :open="isSuccessModalOpen"
        :showDashboardButton="state.showDashboardButton"
        :showHereButton="state.showHereButton"
        @close="hideSuccessModal"
    />
</template>


